import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ChurchFlowLogo from "@/components/ChurchFlowLogo";
import Footer from "@/components/Footer";
import { ArrowRight, CheckCircle2, ClipboardSignature, Banknote, Repeat, UserCog, FileBarChart, Smartphone, ShieldCheck, HeartHandshake, Users } from "lucide-react";

export default function LandingPage() {
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
  }, [user, nav]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <ChurchFlowLogo size={36} className="soft-glow rounded-xl" />
            <div className="font-serif text-xl tracking-tight">ChurchFlow</div>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <a href="#features" className="text-foreground/75 hover:text-foreground transition-colors">Features</a>
            <a href="#how" className="text-foreground/75 hover:text-foreground transition-colors">How it works</a>
            <a href="#about" className="text-foreground/75 hover:text-foreground transition-colors">About</a>
            <a href="#pricing" className="text-foreground/75 hover:text-foreground transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-foreground/75 hover:text-foreground hidden sm:block" data-testid="landing-signin-link">Sign in</Link>
            <Link to="/register" data-testid="landing-cta-top">
              <Button className="rounded-xl soft-glow">Start free <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs uppercase tracking-[0.18em] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" /> Built for churches & nonprofits
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-[64px] leading-[1.04] tracking-tight mt-6">
              Stewardship made <span className="text-primary">simple</span>.
            </h1>
            <p className="text-lg text-muted-foreground mt-6 leading-relaxed max-w-lg">
              ChurchFlow keeps every tithe, offering, and bill in one trustworthy ledger. Mobile counting, two-counter verification, automatic deposits, end-of-year statements — all without a CPA.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="landing-hero-ctas">
              <Link to="/register">
                <Button size="lg" className="rounded-xl soft-glow h-12 px-6 text-base" data-testid="landing-start-button">
                  Create your church account <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-xl h-12 px-6 text-base">Sign in</Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> No setup fees</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Pre-seeded categories</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Mobile counter portal</div>
            </div>
          </div>

          {/* Visual card */}
          <div className="relative fade-up">
            <div className="absolute -inset-6 bg-primary/10 blur-3xl rounded-3xl" aria-hidden />
            <Card className="relative rounded-3xl border-border shadow-xl p-6 bg-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">June 2026 · Pulse</div>
                  <div className="font-serif text-2xl mt-1">Grace Community</div>
                </div>
                <div className="px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium">On track</div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-5">
                <Mini k="Income" v="$18,420" tone="text-primary" />
                <Mini k="Expense" v="$12,180" tone="text-expense" />
                <Mini k="Net" v="$6,240" tone="text-primary" />
              </div>
              <div className="mt-5 rounded-2xl bg-secondary/40 p-4">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Coverage</span><span>117% of obligations</span>
                </div>
                <div className="h-2.5 bg-muted rounded-full overflow-hidden mt-2">
                  <div className="h-full bg-primary w-[100%]" />
                </div>
              </div>
              <div className="mt-5 divide-y divide-border">
                {[
                  { k: "Sunday Tithes · 6/7", v: "+$4,820", up: true },
                  { k: "Office Rent · monthly", v: "-$2,400", up: false },
                  { k: "Online Giving · 6/9", v: "+$1,150", up: true },
                ].map((r) => (
                  <div key={r.k} className="py-2.5 flex items-center justify-between text-sm">
                    <span className="text-foreground/80">{r.k}</span>
                    <span className={`font-mono tabular-nums ${r.up ? "text-primary" : "text-expense"}`}>{r.v}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-secondary/30 border-y border-border">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary">Features</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Everything your treasurer wished they had.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            <Feature icon={Banknote} title="One trustworthy ledger" desc="Track income, expenses, funds, and donors in one place. Pre-seeded with church-specific categories." />
            <Feature icon={ClipboardSignature} title="Two-counter offerings" desc="Mobile count form with denominations, checks, card fees. Both counters digitally sign — verified, time-stamped, audit-proof." />
            <Feature icon={Smartphone} title="Mobile counter portal" desc="Counters log offerings from their phone. Treasurers monitor financial pulse from anywhere." />
            <Feature icon={Repeat} title="Recurring rent & tithes" desc="Set rent, salaries, recurring gifts on a schedule. Post-now or auto-project against the month." />
            <Feature icon={UserCog} title="Role-based access" desc="Admins, treasurers, counters, viewers — each with the right keys. Invite via shareable link." />
            <Feature icon={FileBarChart} title="Statements & reports" desc="One-click giving statements, P&L PDFs, headquarters tithing reports, CSV / Excel exports." />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
          <div className="text-[11px] uppercase tracking-[0.22em] text-primary">How it works</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 max-w-xl">From the offering plate to the bank, in four steps.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {[
              { n: "01", t: "Counters log it", d: "Two counters tally cash, checks, and card gifts on the mobile portal." },
              { n: "02", t: "Both sign", d: "Digital signature with timestamp seals the count as verified." },
              { n: "03", t: "Mark deposited", d: "Treasurer batches verified counts and confirms the bank deposit." },
              { n: "04", t: "Books update", d: "Income posts automatically to the ledger, funds, and donor records." },
            ].map((s) => (
              <Card key={s.n} className="rounded-2xl border-border shadow-none p-6">
                <div className="font-serif text-3xl text-primary tabular-nums">{s.n}</div>
                <div className="font-serif text-xl mt-3">{s.t}</div>
                <p className="text-sm text-muted-foreground mt-2">{s.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary-foreground/80">About ChurchFlow</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Built by ministry people, for ministry people.</h2>
            <p className="mt-6 text-primary-foreground/85 leading-relaxed text-lg">
              We've sat through too many board meetings where the numbers didn't add up. ChurchFlow is the tool we wanted —
              opinionated about good stewardship practice, gentle about everything else. Cash counts are double-signed, deposits batch into the ledger, and every donor gets a clean year-end statement without the late-night spreadsheet panic.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { k: "100%", v: "Audit-ready" },
                { k: "2-counter", v: "Verification" },
                { k: "0", v: "Spreadsheets" },
              ].map((s) => (
                <div key={s.v} className="rounded-2xl bg-white/10 backdrop-blur border border-white/15 p-4">
                  <div className="font-serif text-2xl">{s.k}</div>
                  <div className="text-[11px] uppercase tracking-wider text-primary-foreground/70 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            {[
              { icon: ShieldCheck, t: "Trust by design", d: "Two-counter signatures, immutable verification timestamps." },
              { icon: HeartHandshake, t: "Donor-centered", d: "Year-end giving statements ready in seconds — your members will thank you." },
              { icon: Users, t: "Right-sized teams", d: "From a 30-seat plant to a multi-site congregation, roles scale with you." },
            ].map((b) => (
              <div key={b.t} className="rounded-2xl bg-white/10 backdrop-blur border border-white/15 p-5 flex items-start gap-4">
                <div className="p-2.5 rounded-xl bg-white/20"><b.icon className="w-5 h-5" /></div>
                <div>
                  <div className="font-serif text-xl">{b.t}</div>
                  <p className="text-sm text-primary-foreground/80 mt-1">{b.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section id="pricing">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-20 text-center">
          <div className="text-[11px] uppercase tracking-[0.22em] text-primary">Pricing</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Free while we grow together.</h2>
          <p className="mt-5 text-muted-foreground leading-relaxed text-lg">
            Right now ChurchFlow is free for early ministry partners. No card on file. No "free for 14 days" gimmicks. When we add paid features (PDF mailing, multi-site, advanced budgeting), your existing org stays grandfathered on a generous starter tier.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="rounded-xl soft-glow h-12 px-6 text-base" data-testid="landing-pricing-cta">
                Create your church account <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="rounded-xl h-12 px-6 text-base">I already have an account</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Mini({ k, v, tone }) {
  return (
    <div className="rounded-xl bg-secondary/40 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
      <div className={`font-mono tabular-nums text-lg mt-0.5 ${tone}`}>{v}</div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <Card className="rounded-2xl border-border shadow-none p-6 h-full hover:shadow-md transition-shadow">
      <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <div className="font-serif text-xl mt-4">{title}</div>
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
    </Card>
  );
}
